/**
 * Event Color Context
 * Provides user's custom event type color settings globally
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { EventColor, EVENT_TYPE_COLORS } from '../lib/eventColors';
import { apiGet, apiPut } from '../lib/api';
import { auth } from '../lib/auth';

interface EventColorContextType {
  eventTypeColors: Record<string, EventColor>;
  loading: boolean;
  refreshColors: () => Promise<void>;
}

const EventColorContext = createContext<EventColorContextType | undefined>(undefined);

export function EventColorProvider({ children }: { children: ReactNode }) {
  const [eventTypeColors, setEventTypeColors] = useState<Record<string, EventColor>>(EVENT_TYPE_COLORS);
  const [loading, setLoading] = useState(true);

  const loadColors = async () => {
    try {
      setLoading(true);
      // Only load from API if authenticated
      if (!auth.getToken()) {
         setEventTypeColors(EVENT_TYPE_COLORS);
         setLoading(false);
         return;
       }

       const result = await apiGet('/settings/event-type-colors');

       if (result.success && result.data) {
         // Merge user settings with defaults
         setEventTypeColors({
           ...EVENT_TYPE_COLORS,
           ...result.data,
         });
       } else {
         setEventTypeColors(EVENT_TYPE_COLORS);
       }
     } catch (error) {
       console.error('Failed to load event type colors:', error);
       // Fall back to defaults on error
       setEventTypeColors(EVENT_TYPE_COLORS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadColors();
  }, []);

  const refreshColors = async () => {
    await loadColors();
  };

  return (
    <EventColorContext.Provider value={{ eventTypeColors, loading, refreshColors }}>
      {children}
    </EventColorContext.Provider>
  );
}

export function useEventColors() {
  const context = useContext(EventColorContext);
  if (context === undefined) {
    throw new Error('useEventColors must be used within an EventColorProvider');
  }
  return context;
}

